import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Headphones, Send, LogOut, Image as ImageIcon, X, Globe, DollarSign, MessageCircle, TrendingUp, Users, LifeBuoy, BookOpen, Save, AlertTriangle, Zap, Timer, Award, Loader2, Trophy, Crown, FileText, MapPin } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import OperatorSocialChat from '../../components/operator/SocialChat';
import ContactCoach from '../../components/operator/ContactCoach';

import API_URL from '../../utils/apiUrl';

const OperatorInbox = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();

  // Queue state
  const [queueConv, setQueueConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [queueEmpty, setQueueEmpty] = useState(false);
  const [queueRemaining, setQueueRemaining] = useState(0);
  const [queueLoading, setQueueLoading] = useState(false);
  const [delayCountdown, setDelayCountdown] = useState(0);
  const [operatorScore, setOperatorScore] = useState(0);
  const [operatorDelay, setOperatorDelay] = useState(0);

  // Chat state
  const [newMessage, setNewMessage] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [showLogbook, setShowLogbook] = useState(false);
  const [profileGallery, setProfileGallery] = useState([]);
  const [operatorGallery, setOperatorGallery] = useState([]);

  // Logbook
  const [customerNotes, setCustomerNotes] = useState('');
  const [profileNotes, setProfileNotes] = useState('');
  const [logbookEntries, setLogbookEntries] = useState([]);
  const [savingLogbook, setSavingLogbook] = useState(false);

  // Stats & UI
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('inbox');
  const messagesEndRef = useRef(null);
  const countdownRef = useRef(null);

  const defaultAvatar = 'https://images.unsplash.com/photo-1593704345154-791a1430ac8f?w=100&h=100&fit=crop';

  // ===== QUEUE: Fetch Next Conversation =====
  const fetchNextFromQueue = useCallback(async () => {
    setQueueLoading(true);
    setQueueConv(null);
    setMessages([]);
    setShowGallery(false);
    setShowLogbook(false);
    setNewMessage('');
    setImageUrl('');
    try {
      const isHub = process.env.REACT_APP_MODE === 'hub';
      const res = await axios.get(`${API_URL}/api/operator/queue/next${isHub ? '?hub=1' : ''}`, { headers: getAuthHeader() });
      const data = res.data;
      setOperatorScore(data.score || 0);
      setOperatorDelay(data.delay || 0);
      setQueueRemaining(data.remaining || 0);

      if (data.queue_empty || !data.conversation) {
        setQueueEmpty(true);
        setQueueConv(null);
      } else {
        setQueueEmpty(false);
        setQueueConv(data.conversation);
        setMessages(data.conversation.messages || []);
      }
    } catch (error) {
      toast.error('Failed to load queue');
    }
    setQueueLoading(false);
  }, [getAuthHeader]);

  // ===== QUEUE: Auto-advance after reply with delay =====
  const advanceQueue = useCallback(async () => {
    // Release current lock
    try {
      await axios.post(`${API_URL}/api/operator/queue/release`, {}, { headers: getAuthHeader() });
    } catch {}

    const delay = operatorDelay;
    if (delay > 0) {
      setDelayCountdown(delay);
      setQueueConv(null);
      setMessages([]);

      countdownRef.current = setInterval(() => {
        setDelayCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            fetchNextFromQueue();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      fetchNextFromQueue();
    }
  }, [operatorDelay, fetchNextFromQueue, getAuthHeader]);

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, []);

  // Session heartbeat — pings /api/operator/session/ping every 60s so master
  // can track total online hours. Auto-closes on unmount (logout / tab close).
  useEffect(() => {
    const ping = () => {
      axios.post(`${API_URL}/api/operator/session/ping`, {}, { headers: getAuthHeader() }).catch(() => {});
    };
    ping();
    const t = setInterval(ping, 60000);
    return () => {
      clearInterval(t);
      // Best-effort close on tab close / logout
      try {
        navigator.sendBeacon?.(`${API_URL}/api/operator/session/end`);
      } catch (e) { /* ignore */ }
    };
  }, [getAuthHeader]);

  // Initial load
  useEffect(() => {
    if (activeTab === 'inbox') {
      fetchNextFromQueue();
      fetchStats();
      fetchLeaderboard();
      fetchOperatorGallery();
    }
    if (activeTab === 'stats') {
      fetchLeaderboard();
      fetchStats();
    }
  }, [activeTab]);

  // Poll for messages in current conversation (every 2s)
  const [polling, setPolling] = useState(false);
  useEffect(() => {
    if (!queueConv) return;
    const interval = setInterval(async () => {
      try {
        setPolling(true);
        const res = await axios.get(
          `${API_URL}/api/operator/conversation/${queueConv.managed_profile_id}/${queueConv.user_id}`,
          { headers: getAuthHeader() }
        );
        const newMsgs = res.data.messages || [];
        if (newMsgs.length !== messages.length) {
          setMessages(newMsgs);
        }
      } catch {}
      setPolling(false);
    }, 2000);
    return () => clearInterval(interval);
  }, [queueConv, getAuthHeader, messages.length]);

  // Auto-refresh queue when empty (check every 5s for new conversations)
  useEffect(() => {
    if (!queueEmpty || queueLoading || delayCountdown > 0) return;
    const interval = setInterval(() => {
      fetchNextFromQueue();
    }, 5000);
    return () => clearInterval(interval);
  }, [queueEmpty, queueLoading, delayCountdown, fetchNextFromQueue]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ===== STATS =====
  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/operator/my-stats`, { headers: getAuthHeader() });
      setStats(res.data);
    } catch {}
  };

  // ===== LEADERBOARD =====
  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/operator/queue/leaderboard`, { headers: getAuthHeader() });
      setLeaderboard(res.data.leaderboard || []);
    } catch {}
  };

  // ===== GALLERY =====
  const fetchOperatorGallery = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/operator/gallery`, { headers: getAuthHeader() });
      setOperatorGallery(res.data.images || []);
    } catch {}
  };

  const fetchProfileGallery = async (profileId) => {
    try {
      const res = await axios.get(`${API_URL}/api/gallery/${profileId}?include_private=true`, { headers: getAuthHeader() });
      setProfileGallery(res.data.gallery || []);
    } catch {}
  };

  const selectGalleryImage = (url) => {
    setImageUrl(url);
    toast.success('Image selected');
  };

  // ===== LOGBOOK =====
  const fetchLogbook = async () => {
    if (!queueConv) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/operator/logbook/${queueConv.managed_profile_id}/${queueConv.user_id}`,
        { headers: getAuthHeader() }
      );
      setCustomerNotes(res.data.customer_notes || '');
      setProfileNotes(res.data.profile_notes || '');
      setLogbookEntries(res.data.entries || []);
    } catch {}
  };

  const saveLogbook = async () => {
    if (!queueConv) return;
    setSavingLogbook(true);
    try {
      await axios.put(
        `${API_URL}/api/operator/logbook/${queueConv.managed_profile_id}/${queueConv.user_id}`,
        { customer_notes: customerNotes, profile_notes: profileNotes },
        { headers: getAuthHeader() }
      );
      toast.success('Logbook saved');
    } catch { toast.error('Failed to save'); }
    setSavingLogbook(false);
  };

  // Load logbook & gallery when conversation changes
  useEffect(() => {
    if (queueConv) {
      fetchLogbook();
      fetchProfileGallery(queueConv.managed_profile_id);
    }
  }, [queueConv?.user_id, queueConv?.managed_profile_id]);

  // ===== REPORT ABUSE =====
  const reportAbuse = async (msg) => {
    if (!queueConv) return;
    try {
      await axios.post(`${API_URL}/api/operator/report-abuse`, {
        message_id: msg.id,
        message_content: msg.content,
        reported_user_id: msg.sender_id === queueConv.managed_profile_id ? queueConv.user_id : msg.sender_id,
        reported_user_type: msg.sender_id === queueConv.managed_profile_id ? 'operator' : 'customer'
      }, { headers: getAuthHeader() });
      toast.success('Abuse reported');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to report');
    }
  };

  // ===== SEND MESSAGE =====
  const sendGhostMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !queueConv) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('managed_profile_id', queueConv.managed_profile_id);
      formData.append('receiver_id', queueConv.user_id);
      formData.append('content', newMessage);
      if (imageUrl) formData.append('image_attachment_url', imageUrl);

      await axios.post(`${API_URL}/api/operator/ghostwrite`, formData, {
        headers: getAuthHeader()
      });

      setNewMessage('');
      setImageUrl('');
      fetchStats();
      toast.success('Message sent! Loading next...');

      // Auto-advance after a brief moment to show the sent message
      setTimeout(() => {
        setSending(false);
        advanceQueue();
      }, 800);
      return;
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.startsWith('ACCOUNT_DISABLED:')) {
        toast.error('Your account has been suspended due to a policy violation. You will be logged out.');
        setTimeout(() => { logout(); navigate('/operator/login'); }, 2000);
        setSending(false);
        return;
      }
      toast.error('Failed to send message');
    }
    setSending(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/operator/login');
  };

  // ===== SCORE BADGE COLOR =====
  const getScoreColor = (score) => {
    if (score >= 70) return 'bg-green-500 text-white';
    if (score >= 40) return 'bg-amber-500 text-white';
    return 'bg-red-500 text-white';
  };

  const getDelayLabel = (delay) => {
    if (delay === 0) return 'Instant';
    return `${delay}s`;
  };

  return (
    <div className="h-screen flex flex-col md:flex-row font-admin-body" data-testid="operator-inbox">
      {/* Sidebar */}
      <div className="w-full md:w-80 border-r border-slate-200 bg-white flex flex-col md:h-screen h-auto md:max-h-screen">
        {/* Header */}
        <div className="p-3 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-blue-600">
              <Headphones className="h-4 w-4 text-white" />
            </div>
            <span className="font-admin-heading font-semibold text-sm">Operator Hub</span>
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="operator-logout">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
        <div className="px-3 pt-2">
          <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => navigate('/operator/payslips')} data-testid="operator-payslips-link">
            <FileText className="h-3 w-3 mr-1.5" /> My Payslips
          </Button>
        </div>

        {/* Tab Buttons */}
        <div className="flex border-b border-slate-200 shrink-0">
          <button
            onClick={() => setActiveTab('inbox')}
            className={`flex-1 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors ${activeTab === 'inbox' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-inbox"
          >
            <Zap className="h-3.5 w-3.5" /> Queue
          </button>
          <button
            onClick={() => setActiveTab('team')}
            className={`flex-1 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors ${activeTab === 'team' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-team"
          >
            <Users className="h-3.5 w-3.5" /> Team Chat
          </button>
          <button
            onClick={() => setActiveTab('coach')}
            className={`flex-1 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors ${activeTab === 'coach' ? 'text-green-600 border-b-2 border-green-600' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-coach"
          >
            <LifeBuoy className="h-3.5 w-3.5" /> COACH
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`flex-1 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors ${activeTab === 'stats' ? 'text-amber-600 border-b-2 border-amber-600' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-stats"
          >
            <Trophy className="h-3.5 w-3.5" /> Rank
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'inbox' ? (
          <>
            {/* Queue Status Panel */}
            <div className="p-3 space-y-3">
              {/* Performance Score */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 flex items-center gap-1"><Award className="h-3 w-3" /> Performance</span>
                  <Badge className={getScoreColor(operatorScore)} data-testid="operator-score">{operatorScore.toFixed(0)}pts</Badge>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full transition-all duration-500" style={{ width: `${Math.min(operatorScore, 100)}%` }}></div>
                </div>
                <div className="flex items-center justify-between mt-2 text-[10px] text-slate-400">
                  <span className="flex items-center gap-1"><Timer className="h-3 w-3" /> Queue delay: {getDelayLabel(operatorDelay)}</span>
                  <span>{queueRemaining} remaining</span>
                </div>
              </div>

              {/* Queue State */}
              {queueLoading ? (
                <div className="flex flex-col items-center py-4 text-slate-500">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500 mb-2" />
                  <span className="text-xs">Finding next conversation...</span>
                </div>
              ) : delayCountdown > 0 ? (
                <div className="flex flex-col items-center py-4" data-testid="queue-delay-countdown">
                  <div className="relative w-16 h-16 mb-3">
                    <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                      <circle cx="32" cy="32" r="28" fill="none" stroke="#e2e8f0" strokeWidth="4" />
                      <circle cx="32" cy="32" r="28" fill="none" stroke="#3b82f6" strokeWidth="4"
                        strokeDasharray={`${(delayCountdown / operatorDelay) * 176} 176`}
                        strokeLinecap="round" className="transition-all duration-1000" />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-blue-600">{delayCountdown}</span>
                  </div>
                  <span className="text-xs text-slate-500">Next conversation in...</span>
                  <span className="text-[10px] text-slate-400 mt-1">Higher score = shorter wait</span>
                </div>
              ) : queueEmpty ? (
                <div className="text-center py-4">
                  <Headphones className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm text-slate-500">Queue is empty</p>
                  <p className="text-xs text-slate-400 mb-3">No messages waiting for response</p>
                  <Button size="sm" variant="outline" onClick={fetchNextFromQueue} data-testid="refresh-queue">
                    <Loader2 className="h-3 w-3 mr-1" /> Refresh Queue
                  </Button>
                </div>
              ) : queueConv ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="queue-active-conv">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                    <span className="text-xs font-medium text-blue-700">Now handling</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={(() => { const av = queueConv.sender?.main_avatar || defaultAvatar; return av.startsWith('http') ? av : `${API_URL}${av}`; })()} />
                      <AvatarFallback>{queueConv.sender?.username?.[0] || '?'}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{queueConv.sender?.username}</p>
                      <p className="text-[10px] text-slate-500">As: {queueConv.managed_profile?.username} | {queueConv.message_count} msgs</p>
                      {queueConv.site_label && (
                        <p className="text-[10px] mt-0.5">
                          <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold" data-testid="site-label-badge">
                            {queueConv.site_flag && <span className="mr-1">{queueConv.site_flag}</span>}
                            {queueConv.site_label}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Stats */}
            <div className="mt-auto p-3 border-t border-slate-200 bg-slate-50 space-y-1.5 shrink-0 text-xs">
              {stats && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500 flex items-center gap-1"><MessageCircle className="h-3 w-3" /> Messages</span>
                    <span className="font-medium">{stats.total_messages}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500 flex items-center gap-1"><DollarSign className="h-3 w-3" /> This Week</span>
                    <span className="font-medium text-green-600">${stats.week_earning?.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Total</span>
                    <span className="font-medium text-amber-600">${stats.total_earning?.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          </>
        ) : activeTab === 'team' ? (
          <div className="flex-1 min-h-0">
            <OperatorSocialChat getAuthHeader={getAuthHeader} userId={user?.id} />
          </div>
        ) : activeTab === 'coach' ? (
          <div className="flex-1 min-h-0">
            <ContactCoach getAuthHeader={getAuthHeader} userId={user?.id} />
          </div>
        ) : activeTab === 'stats' ? (
          <div className="flex-1 overflow-auto p-3 space-y-3" data-testid="leaderboard-panel">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                <Trophy className="h-4 w-4 text-amber-500" /> Leaderboard
              </h3>
              <Button variant="ghost" size="sm" onClick={fetchLeaderboard} className="h-7 text-xs">
                <Loader2 className="h-3 w-3 mr-1" /> Refresh
              </Button>
            </div>
            <div className="space-y-1.5">
              {leaderboard.map((entry) => (
                <div
                  key={entry.operator_id}
                  className={`rounded-lg p-2.5 border transition-all ${
                    entry.is_current_user
                      ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`leaderboard-entry-${entry.rank}`}
                >
                  <div className="flex items-center gap-2">
                    {/* Rank */}
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      entry.rank === 1 ? 'bg-amber-100 text-amber-700' :
                      entry.rank === 2 ? 'bg-slate-100 text-slate-600' :
                      entry.rank === 3 ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-50 text-slate-500'
                    }`}>
                      {entry.rank === 1 ? <Crown className="h-3.5 w-3.5" /> : `#${entry.rank}`}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-xs font-medium truncate ${entry.is_current_user ? 'text-blue-700' : 'text-slate-800'}`}>
                          {entry.username}
                        </span>
                        {entry.is_current_user && <Badge className="text-[9px] px-1 py-0 bg-blue-500">You</Badge>}
                        {entry.is_online && <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0"></span>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                        <span>{entry.response_rate}% rate</span>
                        <span>{entry.total_messages} msgs</span>
                        <span className="text-green-600 font-medium">${entry.total_earning}</span>
                      </div>
                    </div>

                    {/* Score */}
                    <div className="text-right shrink-0">
                      <Badge className={`text-[10px] px-1.5 ${
                        entry.score >= 70 ? 'bg-green-500 text-white' :
                        entry.score >= 40 ? 'bg-amber-500 text-white' :
                        'bg-red-500 text-white'
                      }`}>{entry.score}pts</Badge>
                      <div className="text-[9px] text-slate-400 mt-0.5">
                        {entry.delay_seconds === 0 ? 'Instant' : `${entry.delay_seconds}s delay`}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {leaderboard.length === 0 && (
                <div className="text-center py-6 text-slate-400 text-xs">No operators online</div>
              )}
            </div>

            {/* My Stats */}
            {stats && (
              <div className="mt-3 bg-slate-50 rounded-lg p-3 border border-slate-200">
                <h4 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" /> Your Stats
                </h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Messages</span>
                    <span className="font-medium">{stats.total_messages}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">This Week</span>
                    <span className="font-medium">{stats.week_messages}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Week $</span>
                    <span className="font-medium text-green-600">${stats.week_earning?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Total $</span>
                    <span className="font-medium text-amber-600">${stats.total_earning?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}

        
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-slate-50">
        {/* Loading/Countdown State */}
        {(queueLoading || delayCountdown > 0) && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center" data-testid="queue-loading-main">
              <div className="relative w-24 h-24 mx-auto mb-6">
                {delayCountdown > 0 ? (
                  <>
                    <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                      <circle cx="48" cy="48" r="42" fill="none" stroke="#e2e8f0" strokeWidth="6" />
                      <circle cx="48" cy="48" r="42" fill="none" stroke="#3b82f6" strokeWidth="6"
                        strokeDasharray={`${(delayCountdown / operatorDelay) * 264} 264`}
                        strokeLinecap="round" className="transition-all duration-1000" />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-blue-600">{delayCountdown}</span>
                  </>
                ) : (
                  <div className="w-24 h-24 flex items-center justify-center">
                    <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
                  </div>
                )}
              </div>
              <p className="text-lg font-medium text-slate-700">
                {delayCountdown > 0 ? 'Next conversation loading...' : 'Finding a conversation...'}
              </p>
              <p className="text-sm text-slate-400 mt-1">
                {delayCountdown > 0 ? `Your performance delay: ${operatorDelay}s` : 'Checking the queue'}
              </p>
            </div>
          </div>
        )}

        {/* Empty Queue State */}
        {!queueLoading && delayCountdown === 0 && !queueConv && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-slate-500" data-testid="queue-empty-main">
              <Headphones className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">No messages in queue</p>
              <p className="text-sm mb-4 flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Auto-checking for new messages...
              </p>
            </div>
          </div>
        )}

        {/* Active Conversation */}
        {!queueLoading && delayCountdown === 0 && queueConv && (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarImage src={queueConv.sender?.main_avatar || defaultAvatar} />
                  <AvatarFallback>{queueConv.sender?.username?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium">{queueConv.sender?.username}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    Messaging as {queueConv.managed_profile?.username}
                  </div>
                  {queueConv.profile_city && (
                    <div
                      className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
                      title="Tell the customer the profile lives here"
                      data-testid="hub-profile-city"
                    >
                      <MapPin className="h-3 w-3" />
                      Profile lives in {queueConv.profile_city}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2 items-center">
                {polling && <div className="flex items-center gap-1 text-xs text-green-500"><span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>Live</div>}
                <Button
                  variant={showLogbook ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setShowLogbook(!showLogbook); if (showGallery) setShowGallery(false); }}
                  data-testid="toggle-logbook"
                  className={showLogbook ? "bg-amber-500 hover:bg-amber-600" : ""}
                >
                  <BookOpen className="h-4 w-4 mr-1" /> Logbook
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowGallery(!showGallery); if (showLogbook) setShowLogbook(false); }}
                  data-testid="toggle-gallery"
                >
                  <ImageIcon className="h-4 w-4 mr-1" /> Gallery
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 flex">
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_id === queueConv.managed_profile_id ? 'justify-end' : 'justify-start'} group`}
                    >
                      <div className="flex items-end gap-1">
                        {msg.sender_id !== queueConv.managed_profile_id && (
                          <button
                            onClick={() => reportAbuse(msg)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-100 mb-1"
                            title="Report abuse"
                            data-testid={`abuse-btn-${msg.id}`}
                          >
                            <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                          </button>
                        )}
                        <div
                          className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                            msg.sender_id === queueConv.managed_profile_id
                              ? 'bg-blue-500 text-white rounded-tr-sm'
                              : 'bg-white text-slate-800 rounded-tl-sm shadow-sm'
                          }`}
                        >
                          <p>{msg.content}</p>
                          {msg.image_attachment_url && (
                            <img src={msg.image_attachment_url.startsWith('http') ? msg.image_attachment_url : `${API_URL}${msg.image_attachment_url}`} alt="attachment" className="mt-2 rounded-lg max-w-full" />
                          )}
                          {msg.ghostwritten_by && (
                            <p className="text-xs mt-1 opacity-70">Ghostwritten</p>
                          )}
                        </div>
                        {msg.sender_id === queueConv.managed_profile_id && (
                          <button
                            onClick={() => reportAbuse(msg)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-100 mb-1"
                            title="Report abuse"
                            data-testid={`abuse-btn-${msg.id}`}
                          >
                            <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div className="flex justify-end" data-testid="sending-indicator">
                      <div className="bg-blue-100 text-blue-500 rounded-2xl px-4 py-2 flex items-center gap-2 animate-pulse">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                          <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                          <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                        </div>
                        <span className="text-xs">Sending...</span>
                      </div>
                    </div>
                  )}
                  {/* Waiting for reply indicator — shows when last message is from the operator */}
                  {!sending && messages.length > 0 && messages[messages.length - 1]?.sender_id === queueConv?.managed_profile_id && (
                    <div className="flex justify-start" data-testid="waiting-reply-indicator">
                      <div className="bg-slate-100 text-slate-400 rounded-2xl px-4 py-2 flex items-center gap-2">
                        <div className="flex gap-1">
                          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '200ms'}}></span>
                          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '400ms'}}></span>
                        </div>
                        <span className="text-xs">Waiting for reply...</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Gallery Sidebar */}
              {showGallery && (
                <div className="w-72 border-l border-slate-200 bg-white flex flex-col">
                  <div className="p-3 border-b border-slate-200 flex items-center justify-between shrink-0">
                    <span className="text-sm font-medium">Image Gallery</span>
                    <Button variant="ghost" size="icon" onClick={() => setShowGallery(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <ScrollArea className="flex-1">
                    {operatorGallery.length > 0 && (
                      <div className="p-2">
                        <p className="text-xs font-medium text-slate-500 px-1 mb-2">Private Gallery ({operatorGallery.length})</p>
                        <div className="grid grid-cols-3 gap-1.5">
                          {operatorGallery.map((img) => (
                            <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden cursor-pointer ring-1 ring-slate-200 hover:ring-blue-400 transition-all"
                              onClick={() => selectGalleryImage(img.url)} data-testid={`gallery-img-${img.id}`}
                            >
                              <img src={img.url.startsWith('http') ? img.url : `${API_URL}${img.url}`} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {profileGallery.length > 0 && (
                      <div className="p-2 border-t border-slate-100">
                        <p className="text-xs font-medium text-slate-500 px-1 mb-2">Profile Images ({profileGallery.length})</p>
                        <div className="grid grid-cols-3 gap-1.5">
                          {profileGallery.map((img) => (
                            <div key={img.id} className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer ${img.is_private ? 'ring-2 ring-amber-400' : 'ring-1 ring-slate-200'} hover:ring-blue-400 transition-all`}
                              onClick={() => selectGalleryImage(img.image_url)}
                            >
                              <img src={img.image_url.startsWith('http') ? img.image_url : `${API_URL}${img.image_url}`} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                              {img.is_private && <Badge className="absolute top-0.5 right-0.5 text-[8px] px-1 bg-amber-500">P</Badge>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {profileGallery.length === 0 && operatorGallery.length === 0 && (
                      <div className="text-center py-8 text-slate-400 text-sm">No gallery images</div>
                    )}
                  </ScrollArea>
                </div>
              )}

              {/* Logbook Sidebar */}
              {showLogbook && (
                <div className="w-80 border-l border-slate-200 bg-white flex flex-col" data-testid="logbook-panel">
                  <div className="p-3 border-b border-slate-200 flex items-center justify-between shrink-0">
                    <span className="text-sm font-medium flex items-center gap-1">
                      <BookOpen className="h-4 w-4 text-amber-500" /> Operator Logbook
                    </span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={saveLogbook} disabled={savingLogbook} data-testid="save-logbook">
                        <Save className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setShowLogbook(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto p-3 space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-blue-600 mb-1 block">
                        Customer Notes ({queueConv?.sender?.username})
                      </label>
                      <Textarea value={customerNotes} onChange={(e) => setCustomerNotes(e.target.value)}
                        placeholder="Notes about this customer..." className="text-xs min-h-[100px] resize-none border-blue-200 focus:border-blue-400"
                        data-testid="customer-notes" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-purple-600 mb-1 block">
                        Profile Notes ({queueConv?.managed_profile?.username})
                      </label>
                      <Textarea value={profileNotes} onChange={(e) => setProfileNotes(e.target.value)}
                        placeholder="Notes about this managed profile..." className="text-xs min-h-[100px] resize-none border-purple-200 focus:border-purple-400"
                        data-testid="profile-notes" />
                    </div>
                    <Button onClick={saveLogbook} disabled={savingLogbook} className="w-full bg-amber-500 hover:bg-amber-600 text-white text-xs" data-testid="save-logbook-btn">
                      <Save className="h-3 w-3 mr-1" /> {savingLogbook ? 'Saving...' : 'Save Notes'}
                    </Button>
                    {logbookEntries.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 mb-1">Activity Log</p>
                        <div className="space-y-1">
                          {logbookEntries.slice(-10).reverse().map((entry, i) => (
                            <div key={i} className="text-[10px] text-slate-400 flex items-center gap-1">
                              <span className="font-medium text-slate-600">{entry.operator_name}</span>
                              <span>{entry.action}</span>
                              <span className="ml-auto">{new Date(entry.timestamp).toLocaleDateString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Message Input */}
            <form onSubmit={sendGhostMessage} className="bg-white border-t border-slate-200 p-4">
              {imageUrl && (
                <div className="mb-2 flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <img src={imageUrl} alt="Selected" className="h-12 w-12 object-cover rounded" />
                  <span className="text-sm text-slate-600 flex-1 truncate">{imageUrl}</span>
                  <Button variant="ghost" size="icon" onClick={() => setImageUrl('')}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Textarea
                  placeholder={`Reply as ${queueConv.managed_profile?.username}...`}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="flex-1 min-h-[40px] max-h-32 resize-none"
                  rows={1}
                  data-testid="ghost-message-input"
                />
                <Button type="submit" disabled={sending || !newMessage.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="send-ghost-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default OperatorInbox;
